import React from 'react';
import { FeedbackDashboardCardProps } from 'shared/components/Feedback/FeedbackDashboardCard';
import ReportStatus, {
  ReportStatusModal,
} from 'shared/components/Feedback/ReportStatus';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { capitalizeFirstLetter } from 'shared/utils/stringUtils';
import styled from 'styled-components';
import TrueImpactCapsule from 'shared/design-system/components/TrueImpactCapsule';
import { formattedDate } from 'shared/utils/date';

const parseEmails = (emails: string | string[]) => {
  if (typeof emails === 'string') {
    return [emails];
  } else if (Array.isArray(emails)) {
    return emails;
  }
  return [];
};

const parseContactEmails = (
  feedback: FeedbackDashboardCardProps,
): { donor: string[]; npo: string[] } => {
  const result: { donor: string[]; npo: string[] } = { donor: [], npo: [] };
  feedback.contacts.forEach(contact => {
    if (contact.accountType === 'donor' && contact.email) {
      result.donor = [...result.donor, ...parseEmails(contact.email)];
    }
    if (contact.accountType === 'npo' && contact.email) {
      result.npo = [...result.npo, ...parseEmails(contact.email)];
    }
  });
  return result;
};

interface FeedbackHeaderProps {
  feedback: FeedbackDashboardCardProps;
}

const StyledDiv = styled.div`
  padding: ${SPACERS.medium} ${SPACERS.large};
  .feedback-header-top {
    display: flex;
    gap: ${SPACERS.medium};
    .feedback-header {
      flex: 1;
    }
    .feedback-header-label {
      margin-bottom: 20px;
    }
    .feedback-header-title {
      margin-bottom: ${SPACERS.small};
    }
    .feedback-header-donor {
      margin-bottom: 19px;
    }
  }
  .feedback-report-capsule {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: ${SPACERS.small};
    .feedback-report-capsule-left {
      display: flex;
      gap: ${SPACERS.small};
    }
  }
`;

const FeedbackHeader: React.FC<FeedbackHeaderProps> = ({ feedback }) => {
  const { donor, npo } = parseContactEmails(feedback);
  return (
    <StyledDiv>
      <div className="feedback-header-top">
        <div className="feedback-header">
          <TrueImpactText
            variant="heading-label"
            color="blackBase"
            className="feedback-header-label"
          >
            REPORT FEEDBACK
          </TrueImpactText>
          <TrueImpactText
            variant="heading-page"
            className="feedback-header-title"
          >
            {feedback.year} {capitalizeFirstLetter(feedback.title)}
          </TrueImpactText>
          <TrueImpactText variant="heading" className="feedback-header-donor">
            {feedback.donorName}
          </TrueImpactText>
        </div>
        <div className="feedback-report-status">
          <ReportStatusModal
            dbId={feedback.id}
            year={feedback.year}
            stage={feedback.stage}
            status={feedback.status}
            marginTop={0}
            marginBottom={0}
            borderRadius={0}
            feedbackType={feedback.feedbackType}
            reportingType={feedback.reportingType}
            optedInFullReporting={feedback.optedInFullReporting}
            padding={`${SPACERS.tiny} ${SPACERS.small}`}
            hideText
          />
        </div>
      </div>
      <div className="feedback-report-capsule">
        <div className="feedback-report-capsule-left">
          <TrueImpactCapsule variant="body">
            <strong>Due:</strong> {formattedDate(feedback.dueDate)}
          </TrueImpactCapsule>
          <TrueImpactCapsule variant="body" textSelection={true}>
            <strong>NPO:</strong> {npo[0]}
          </TrueImpactCapsule>
        </div>
        <div>
          <TrueImpactCapsule variant="body" textSelection={true}>
            <strong>Feedback Contacts:</strong> {donor[0] || 'No donors'} |
            support@trueimpact.com
          </TrueImpactCapsule>
        </div>
      </div>
    </StyledDiv>
  );
};

export default React.memo(FeedbackHeader);
